import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Briefcase, ChevronLeft, X, Upload, ArrowRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import usePageMeta from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CareerDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [missing, setMissing] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/jobs/by-slug/${slug}`).then(r => setJob(r.data)).catch(() => setMissing(true));
  }, [slug]);

  usePageMeta({
    title: job ? `${job.title} · Careers at Adviserz` : "Career · Adviserz",
    description: job ? `${job.title} — ${job.location} · ${job.employment_type}. ${job.description.slice(0, 140)}` : "Open role at Adviserz.",
    canonical: `https://adviserz.in/careers/${slug}`,
  });

  if (missing) {
    return (
      <div className="min-h-screen bg-background text-white">
        <SiteHeader />
        <div className="max-w-3xl mx-auto px-6 py-32 text-center">
          <h1 className="font-display font-bold text-3xl">Role no longer open.</h1>
          <p className="mt-3 text-muted-foreground">It may have been filled or removed.</p>
          <button onClick={() => navigate("/careers")} className="mt-6 px-5 py-2.5 rounded-full bg-white text-background font-semibold">
            See open roles
          </button>
        </div>
        <SiteFooter />
      </div>
    );
  }
  if (!job) return <div className="min-h-screen grid place-items-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#08090B] text-white">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 spotlight-column" />
        <SiteHeader active="careers" />
        <article className="relative max-w-3xl mx-auto px-6 pt-40 pb-24 text-center">
          <Link to="/careers" className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-white">
            <ChevronLeft className="w-3 h-3" /> All roles
          </Link>
          <div className="mt-6">
            <div className="text-sm text-white/60 mb-3 tracking-wide">{job.department}</div>
            <h1 className="font-serif-display font-normal text-4xl sm:text-5xl tracking-tight leading-[1.08]" data-testid="job-detail-title">
              {job.title}
            </h1>
            <div className="mt-6 flex flex-wrap gap-4 justify-center text-sm text-white/60">
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {job.location}</span>
              <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4" /> {job.employment_type}</span>
            </div>
          </div>
        </article>
      </section>

      <article className="max-w-2xl mx-auto px-6 pb-24 text-left">
        <div className="prose prose-invert max-w-none text-base leading-relaxed whitespace-pre-wrap text-white/85 border-t border-white/10 pt-12">
          {job.description}
        </div>

        <div className="mt-12 text-center">
          <button onClick={() => setOpen(true)} data-testid="open-apply-btn"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-white text-background font-medium hover:bg-white/90 transition-colors">
            Apply for this role <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </article>

      {open && <ApplyDialog job={job} onClose={() => setOpen(false)} />}
      <SiteFooter />
    </div>
  );
}

function ApplyDialog({ job, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", cover_letter: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Please upload your resume."); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Resume must be under 5 MB."); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("cover_letter", form.cover_letter);
      fd.append("resume", file);
      await axios.post(`${API}/jobs/${job.id}/apply`, fd);
      toast.success("Application received. We'll be in touch.");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="apply-dialog">
      <div className="bg-card border border-border rounded-md max-w-xl w-full max-h-[90vh] overflow-y-auto my-8">
        <div className="flex items-start justify-between p-6 border-b border-border sticky top-0 bg-card">
          <div>
            <div className="label-xs mb-1">Apply</div>
            <h3 className="font-display font-bold text-xl">{job.title}</h3>
            <div className="text-xs text-muted-foreground mt-1">{job.location} · {job.employment_type}</div>
          </div>
          <button onClick={onClose} data-testid="close-apply" className="p-2 hover:bg-accent rounded-md"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <Field label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="apply-name" />
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="apply-email" />
          <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="apply-phone" />
          <label className="block">
            <div className="label-xs mb-2">Cover Letter</div>
            <textarea value={form.cover_letter} onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
              rows={5} data-testid="apply-cover"
              placeholder="Why Adviserz? What edge do you bring?"
              className="w-full bg-background border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:border-ai resize-y" />
          </label>
          <label className="block">
            <div className="label-xs mb-2">Resume · PDF or DOC (max 5 MB)</div>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-border hover:bg-accent transition-colors cursor-pointer text-sm">
                <Upload className="w-4 h-4" />
                <span>{file ? "Change file" : "Choose file"}</span>
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" data-testid="apply-resume"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
              <span className="text-xs text-muted-foreground truncate">
                {file ? `${file.name} · ${(file.size / 1024).toFixed(0)} KB` : "No file selected"}
              </span>
            </div>
          </label>
          <button type="submit" disabled={loading} data-testid="apply-submit"
            className="w-full mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md bg-white text-background font-semibold hover:bg-white/90 transition-colors disabled:opacity-60">
            {loading ? "Submitting…" : "Submit Application"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, testid }) {
  return (
    <label className="block">
      <div className="label-xs mb-2">{label}</div>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
        required={required} data-testid={testid}
        className="w-full bg-background border border-border rounded-md px-4 py-3 text-sm focus:outline-none focus:border-ai" />
    </label>
  );
}
